/**
 * useMenuItemState - Custom hook for menu item card state management
 * Handles customization state, price calculations, and API calls
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

// Core ingredient detection patterns
const CORE_PATTERNS = [
  { itemKeywords: ['coffee', 'espresso', 'latte', 'cappuccino', 'macchiato', 'mocha', 'americano'], 
    ingredientKeywords: ['coffee', 'espresso', 'shot'] },
  { itemKeywords: ['egg', 'eggs', 'omelette', 'omelet', 'frittata'],
    ingredientKeywords: ['egg', 'eggs'] },
  { itemKeywords: ['chicken wrap', 'chicken sandwich', 'chicken burger', 'grilled chicken'], 
    ingredientKeywords: ['chicken'] },
  { itemKeywords: ['beef burger', 'beef wrap', 'steak'], ingredientKeywords: ['beef', 'steak'] },
  { itemKeywords: ['fish taco', 'fish wrap', 'grilled fish'], ingredientKeywords: ['fish'] },
  { itemKeywords: ['salmon dish', 'grilled salmon', 'salmon fillet'], ingredientKeywords: ['salmon'] },
  { itemKeywords: ['tuna dish', 'tuna salad', 'tuna sandwich'], ingredientKeywords: ['tuna'] },
  { itemKeywords: ['shrimp', 'prawn'], ingredientKeywords: ['shrimp', 'prawn'] },
  { itemKeywords: ['acai'], ingredientKeywords: ['acai'] },
  { itemKeywords: ['fries', 'chips'], ingredientKeywords: ['fries', 'chips'] },
  { itemKeywords: ['rice'], ingredientKeywords: ['rice'] },
  { itemKeywords: ['salad'], ingredientKeywords: ['salad', 'greens', 'lettuce'] },
  { itemKeywords: ['toast'], ingredientKeywords: ['toast', 'bread'] },
];

const BLENDED_PATTERNS = [
  /blended with ([^,]+)/i,
  /made with ([^,]+)/i,
  /([a-z]+) base/i,
];

// Syrup names for exclusive selection (only one syrup at a time, but can add multiple shots)
const SYRUP_NAMES = ['caramel', 'hazelnut', 'vanilla', 'caramel syrup', 'hazelnut syrup', 'vanilla syrup'];

// Check if an add-on is a syrup
const isSyrup = (addonName) => {
  const nameLower = addonName?.toLowerCase() || '';
  return SYRUP_NAMES.some(syrup => nameLower.includes(syrup));
};

export default function useMenuItemState(item, availableAddOns = []) {
  // Core customization data from API
  const [customization, setCustomization] = useState(null);
  const [loadingCustomization, setLoadingCustomization] = useState(false);
  const [popularCustomizations, setPopularCustomizations] = useState(null);
  
  // User selections
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [removedIngredients, setRemovedIngredients] = useState([]);
  const [extraIngredients, setExtraIngredients] = useState([]);
  // Add-ons now store quantity: [{ name, price, quantity }]
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [specialNotes, setSpecialNotes] = useState('');
  const [quantity, setQuantity] = useState(1);

  // Memoized data
  const ingredients = useMemo(() => 
    customization?.ingredients || item.ingredients || [],
    [customization, item]
  );

  const modifiers = useMemo(() => 
    customization?.modifiers || item.modifiers || [],
    [customization, item]
  );

  const sizes = useMemo(() => 
    customization?.sizes || item.sizes || [],
    [customization, item]
  );

  // Global modifier groups (e.g., Milk Type, Sugar Level) from store settings
  // Filter out auto-generated item-level modifier groups to avoid duplication
  const modifierGroups = useMemo(() => {
    const groups = customization?.modifier_groups || [];
    // Filter out groups that duplicate item-level modifiers
    return groups.filter(group => {
      // Skip groups with id starting with 'item-' (auto-generated from item modifiers)
      if (group.id && group.id.startsWith('item-')) return false;
      // Skip groups named 'Options' that are duplicates of item modifiers
      if (group.name === 'Options' && group.type === 'multi') return false;
      return true;
    });
  }, [customization]);

  const hasCustomizations = useMemo(() => 
    item.sizes?.length > 0 || 
    item.modifiers?.length > 0 || 
    item.ingredients?.length > 0 ||
    availableAddOns.length > 0,
    [item, availableAddOns]
  );

  const compatibleAddOns = useMemo(() => {
    const showCategoryAddons = item.allow_category_addons !== false;
    if (!showCategoryAddons) return [];
    
    return availableAddOns.filter(addon => {
      if (!addon.compatible_categories || addon.compatible_categories.length === 0) return true;
      return addon.compatible_categories.includes(item.category);
    });
  }, [item, availableAddOns]);

  // Check if a syrup is already selected (for exclusive selection)
  const getSelectedSyrup = useCallback(() => {
    return selectedAddOns.find(a => isSyrup(a.name));
  }, [selectedAddOns]);

  // Check if an add-on should be disabled (syrup exclusivity)
  const isAddOnDisabled = useCallback((addon) => {
    if (!isSyrup(addon.name)) return false;
    const selectedSyrup = getSelectedSyrup();
    // Disable if another syrup is already selected
    return selectedSyrup && selectedSyrup.name !== addon.name;
  }, [getSelectedSyrup]);

  // Core ingredient detection
  const isCoreIngredient = useCallback((ingredientName) => {
    const itemNameLower = item.name?.toLowerCase() || '';
    const itemDescLower = item.description?.toLowerCase() || '';
    const ingNameLower = ingredientName?.toLowerCase() || '';
    
    if (ingredients.length === 1) return true;
    if (itemNameLower.includes(ingNameLower) || ingNameLower.includes(itemNameLower)) return true;
    
    for (const pattern of CORE_PATTERNS) {
      const itemNameMatches = pattern.itemKeywords.some(k => itemNameLower.includes(k));
      const ingMatches = pattern.ingredientKeywords.some(k => ingNameLower.includes(k));
      if (itemNameMatches && ingMatches) return true;
    }
    
    for (const pattern of BLENDED_PATTERNS) {
      const match = itemDescLower.match(pattern);
      if (match && match[1]) {
        const coreIngredient = match[1].trim().toLowerCase();
        if (ingNameLower.includes(coreIngredient) || coreIngredient.includes(ingNameLower)) return true;
      }
    }
    
    if (ingNameLower.includes('egg') && /\d+\s*eggs?/i.test(itemDescLower)) return true;
    
    return false;
  }, [item, ingredients]);

  // Price calculations
  const getModifierPrice = useCallback((modifier) => {
    const sizeModifiers = customization?.size_modifiers || [];
    const sizeSpecific = sizeModifiers.find(
      sm => sm.modifier_name === modifier.name && sm.size === selectedSize
    );
    // Check size-specific, then price_adjustment (API field), then price (admin field), then 0
    return sizeSpecific?.price || modifier.price_adjustment || modifier.price || 0;
  }, [customization, selectedSize]);

  const calculatePrice = useCallback(() => {
    let price = item.price;
    
    if (selectedSize && sizes.length > 0) {
      const sizeData = sizes.find(s => s.name === selectedSize);
      if (sizeData) price = sizeData.price;
    }
    
    const sizeModifiers = customization?.size_modifiers || [];
    selectedModifiers.forEach(mod => {
      const sizeSpecific = sizeModifiers.find(
        sm => sm.modifier_name === mod.name && sm.size === selectedSize
      );
      // Check size-specific, then price_adjustment (API field), then price (admin field)
      price += sizeSpecific?.price || mod.price_adjustment || mod.price || 0;
    });
    
    extraIngredients.forEach(ing => { price += ing.price || 0; });
    
    // Add-ons now have quantity - multiply price by quantity
    selectedAddOns.forEach(addon => { 
      price += (addon.price || 0) * (addon.quantity || 1); 
    });
    
    return price;
  }, [item.price, selectedSize, sizes, selectedModifiers, extraIngredients, selectedAddOns, customization]);

  const finalPrice = calculatePrice();

  const formatPriceRange = useCallback(() => {
    if (item.sizes && item.sizes.length > 1) {
      const prices = item.sizes.map(s => s.price);
      return `${Math.min(...prices).toFixed(2)} - ${Math.max(...prices).toFixed(2)}`;
    }
    return `${item.price.toFixed(2)}`;
  }, [item]);

  const getIngredientStepNumber = useCallback(() => {
    let step = 1;
    if (sizes.length > 0) step++;
    if (modifiers.length > 0) step++;
    return step;
  }, [sizes, modifiers]);

  // API calls
  const fetchCustomization = async () => {
    setLoadingCustomization(true);
    try {
      // Use customization-full endpoint to get both item customization AND global modifier groups
      const res = await api.get(`/menu/${item.id}/customization-full`);
      const data = res.data;
      setCustomization({
        ...data.customization,
        sizes: data.sizes || data.customization?.sizes || [],
        modifier_groups: data.modifier_groups || []
      });
    } catch (err) {
      console.error('Failed to fetch customization:', err);
      // Fallback to basic customization endpoint
      try {
        const res = await api.get(`/menu/${item.id}/customization`);
        setCustomization(res.data.customization);
      } catch (fallbackErr) {
        console.error('Fallback customization fetch failed:', fallbackErr);
      }
    }
    setLoadingCustomization(false);
  };

  const fetchPopularCustomizations = async () => {
    try {
      const res = await api.get(`/customizations/popular/${item.id}`);
      setPopularCustomizations(res.data);
    } catch (err) {
      console.error('Failed to fetch popular customizations:', err);
    }
  };

  // Event handlers
  const applyPopularCustomization = (customizationItem) => {
    const { name, type, price } = customizationItem;
    
    if (type === 'extra' && !extraIngredients.find(e => e.name === name)) {
      setExtraIngredients(prev => [...prev, { name, price: price || 0 }]);
      toast.success(`Added ${name}`);
    } else if (type === 'modifier' && !selectedModifiers.find(m => m.name === name)) {
      setSelectedModifiers(prev => [...prev, { name, price: price || 0 }]);
      toast.success(`Added ${name}`);
    } else if (type === 'size') {
      setSelectedSize(name);
      toast.success(`Size set to ${name}`);
    }
  };

  const toggleModifier = (modifier) => {
    const price = getModifierPrice(modifier);
    const exists = selectedModifiers.find(m => m.name === modifier.name);
    
    if (exists) {
      setSelectedModifiers(prev => prev.filter(m => m.name !== modifier.name));
    } else {
      setSelectedModifiers(prev => [...prev, { name: modifier.name, price }]);
    }
  };

  // Select an option within a modifier group (radio-style - only one per group)
  const selectModifierGroupOption = (groupName, optionName, optionPrice) => {
    setSelectedModifiers(prev => {
      // Remove any existing selection for this group
      const filtered = prev.filter(m => m.category !== groupName);
      // Add new selection with all required fields for the order
      return [...filtered, {
        name: optionName,
        category: groupName,
        modifier_name: groupName,
        selected_option: optionName,
        price: optionPrice || 0,
        price_adjustment: optionPrice || 0
      }];
    });
  };

  // Deselect a modifier group option (if not required)
  const deselectModifierGroupOption = (groupName) => {
    setSelectedModifiers(prev => prev.filter(m => m.category !== groupName));
  };

  // Get the selected option for a modifier group
  const getSelectedModifierGroupOption = (groupName) => {
    return selectedModifiers.find(m => m.category === groupName);
  };

  const toggleRemoveIngredient = (ingredientName) => {
    if (removedIngredients.includes(ingredientName)) {
      setRemovedIngredients(prev => prev.filter(i => i !== ingredientName));
    } else {
      setRemovedIngredients(prev => [...prev, ingredientName]);
    }
  };

  const toggleExtraIngredient = (ingredient) => {
    const exists = extraIngredients.find(i => i.name === ingredient.name);
    if (exists) {
      setExtraIngredients(prev => prev.filter(i => i.name !== ingredient.name));
    } else {
      setExtraIngredients(prev => [...prev, { name: ingredient.name, price: ingredient.extra_price || 0 }]);
    }
  };

  // Toggle add-on - now supports quantity and syrup exclusivity
  const toggleAddOn = (addon) => {
    const exists = selectedAddOns.find(a => a.name === addon.name);
    
    if (exists) {
      // Remove the add-on
      setSelectedAddOns(prev => prev.filter(a => a.name !== addon.name));
    } else {
      // For syrups: remove any existing syrup first (only one syrup allowed)
      if (isSyrup(addon.name)) {
        setSelectedAddOns(prev => {
          const withoutSyrups = prev.filter(a => !isSyrup(a.name));
          return [...withoutSyrups, { name: addon.name, price: addon.price, quantity: 1 }];
        });
      } else {
        // Regular add-on: just add with quantity 1
        setSelectedAddOns(prev => [...prev, { name: addon.name, price: addon.price, quantity: 1 }]);
      }
    }
  };

  // Increase add-on quantity
  const increaseAddOnQuantity = (addonName) => {
    setSelectedAddOns(prev => prev.map(a => 
      a.name === addonName 
        ? { ...a, quantity: (a.quantity || 1) + 1 }
        : a
    ));
  };

  // Decrease add-on quantity (removes if quantity reaches 0)
  const decreaseAddOnQuantity = (addonName) => {
    setSelectedAddOns(prev => {
      return prev.map(a => {
        if (a.name === addonName) {
          const newQty = (a.quantity || 1) - 1;
          if (newQty <= 0) return null;
          return { ...a, quantity: newQty };
        }
        return a;
      }).filter(Boolean);
    });
  };

  const resetSelections = () => {
    setSelectedSize(null);
    setSelectedModifiers([]);
    setRemovedIngredients([]);
    setExtraIngredients([]);
    setSelectedAddOns([]);
    setSpecialNotes('');
    setQuantity(1);
  };

  // Auto-select first size
  useEffect(() => {
    if ((customization || item) && sizes.length > 0 && !selectedSize) {
      setSelectedSize(sizes[0].name);
    }
  }, [customization, item, sizes, selectedSize]);

  return {
    // Data
    customization,
    loadingCustomization,
    popularCustomizations,
    ingredients,
    modifiers,
    modifierGroups,
    sizes,
    hasCustomizations,
    compatibleAddOns,
    // State
    selectedSize,
    setSelectedSize,
    selectedModifiers,
    removedIngredients,
    extraIngredients,
    selectedAddOns,
    specialNotes,
    setSpecialNotes,
    quantity,
    setQuantity,
    // Calculated
    finalPrice,
    // Helpers
    isCoreIngredient,
    getModifierPrice,
    formatPriceRange,
    getIngredientStepNumber,
    isAddOnDisabled,
    isSyrup,
    getSelectedSyrup,
    // API calls
    fetchCustomization,
    fetchPopularCustomizations,
    // Event handlers
    applyPopularCustomization,
    toggleModifier,
    selectModifierGroupOption,
    deselectModifierGroupOption,
    getSelectedModifierGroupOption,
    toggleRemoveIngredient,
    toggleExtraIngredient,
    toggleAddOn,
    increaseAddOnQuantity,
    decreaseAddOnQuantity,
    resetSelections
  };
}
